'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeft, CheckCircle, Lightbulb } from 'lucide-react'

export default function SnorkelPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen py-4 xxs:py-3 px-3 xxs:px-2 ${
      isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-green-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        <Link
          href="/equipment"
          className={`inline-flex items-center gap-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${
            isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
          장비 가이드 목록
        </Link>

        <article className={`rounded-xl shadow-lg overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* 헤더 */}
          <div className={`p-6 xxs:p-4 ${isDark ? 'bg-green-900/30' : 'bg-green-50'}`}>
            <h1 className={`text-2xl xxs:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              프리다이빙 스노클 선택 가이드
            </h1>
            <p className={`mt-2 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              수면에서 편안한 호흡을 위한 스노클 선택 가이드입니다.
            </p>
          </div>

          <div className="p-6 xxs:p-4 space-y-6 xxs:space-y-4">
            {/* 스노클의 역할 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-3 xxs:mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                스노클의 역할
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  { emoji: '🌊', title: '수면 호흡', desc: '얼굴을 물에 담근 채 호흡' },
                  { emoji: '⚡', title: '에너지 절약', desc: '다이빙 간 휴식 시 편안함' },
                  { emoji: '👁️', title: '시야 확보', desc: '수면 관찰 용이' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-lg">{item.emoji}</span>
                    <span className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {item.title}
                    </span>
                    <span className={`text-sm xxs:text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      : {item.desc}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 프리다이빙 vs 스쿠버 */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <p className={`text-sm xxs:text-xs ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
                <strong>프리다이빙 vs 스쿠버 스노클:</strong> 프리다이빙용은 더 심플하고 가볍습니다.
                복잡한 밸브나 배수 시스템이 없어 유선형이고 저항이 적습니다.
              </p>
            </div>

            {/* 스노클 선택 기준 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                스노클 선택 기준
              </h2>
              <div className="space-y-3 xxs:space-y-2">
                {[
                  {
                    title: '1. 튜브 직경',
                    items: ['표준 직경: 18-20mm (가장 일반적)', '넓은 직경: 20mm+ (호흡 편함, 저항 큼)', '좁은 직경: 16-18mm (저항 적음, 호흡 힘듦)', '추천: 18-19mm가 적절']
                  },
                  {
                    title: '2. 튜브 길이',
                    items: ['너무 길면 호흡 저항 증가', '너무 짧으면 파도에 물 들어옴', '적정 길이: 35-45cm', '개인 체격에 맞게 선택']
                  },
                  {
                    title: '3. 마우스피스',
                    items: ['부드러운 실리콘 소재', '입에 편안하게 물림', '교체 가능한 제품 선택', '턱 피로도 중요']
                  },
                  {
                    title: '4. 디자인',
                    items: ['J자형: 가장 심플, 프리다이빙 표준', '해부학적 곡선: 머리에 밀착', '복잡한 밸브나 장치 없음', '유선형이 최고']
                  },
                ].map((section, idx) => (
                  <div key={idx} className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                    <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-green-300' : 'text-green-800'}`}>
                      {section.title}
                    </h3>
                    <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-1.5">
                          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* 스노클 착용법 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                스노클 착용법
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  { title: '마스크 스트랩에 고정', desc: '왼쪽 귀 위 부근에 부착, 너무 조이지 않게' },
                  { title: '입에 편안하게 물기', desc: '이로 너무 세게 물지 말기, 입술로 감싸듯이' },
                  { title: '다이빙 전 빼기', desc: '하강 전 입에서 빼기, 상승 후 다시 물기' },
                ].map((item, idx) => (
                  <div key={idx} className={`p-3 xxs:p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <p className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {idx + 1}. {item.title}
                    </p>
                    <p className={`text-xs xxs:text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 스노클 클리어링 */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <h3 className={`font-bold text-base xxs:text-sm mb-3 xxs:mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                스노클 클리어링 (물 빼는 방법)
              </h3>
              <div className="space-y-2 xxs:space-y-1.5">
                <div>
                  <p className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    1. 강한 날숨 (Blast Method)
                  </p>
                  <p className={`text-xs xxs:text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    수면 도달 시 강하게 후~ 불기, 가장 빠르고 효과적
                  </p>
                </div>
                <div>
                  <p className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    2. 기울이기
                  </p>
                  <p className={`text-xs xxs:text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    머리를 뒤로 젖혀 물 빼기, 천천히 호흡 시작
                  </p>
                </div>
              </div>
            </div>

            {/* 구매 팁 */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
              <div className="flex items-start gap-2">
                <Lightbulb className={`w-5 h-5 xxs:w-4 xxs:h-4 flex-shrink-0 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <div>
                  <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    구매 팁
                  </h3>
                  <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-yellow-200/80' : 'text-yellow-700'}`}>
                    <li>• 가격대: 2만원 ~ 5만원 (고급 브랜드 10만원+)</li>
                    <li>• 심플한 J자형 추천</li>
                    <li>• 브랜드: Cressi, Mares, Omer, Salvimar</li>
                    <li>• 마우스피스 교체 가능한 제품 선택</li>
                    <li>• 실제로 물어보고 구매</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 스노클 관리 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-3 xxs:mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                스노클 관리
              </h2>
              <div className="space-y-1.5 xxs:space-y-1">
                {[
                  '사용 후 민물로 헹구기',
                  '그늘에서 건조',
                  '마우스피스 정기 교체 (1년마다)',
                  '튜브 내부도 깨끗이 세척',
                  '균열이나 손상 확인',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle className={`w-4 h-4 xxs:w-3.5 xxs:h-3.5 ${isDark ? 'text-green-400' : 'text-green-500'}`} />
                    <span className={`text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 버디 매칭 CTA */}
            <div className={`p-5 xxs:p-4 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-blue-300' : 'text-gray-900'}`}>
                장비 갖추고 버디 찾기
              </h3>
              <p className={`text-sm xxs:text-xs mb-4 xxs:mb-3 ${isDark ? 'text-blue-200/80' : 'text-gray-600'}`}>
                장비가 준비되었다면 이제 함께 다이빙할 버디를 찾아보세요!
              </p>
              <Link
                href="/matching/new"
                className="inline-block px-5 xxs:px-4 py-2.5 xxs:py-2 text-sm xxs:text-xs font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                버디 매칭 신청하기
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
